import { useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useAlerts,
  useAlertSite,
  useAlertSites,
  useAlertStats,
  useClaudeInstruction,
  useRecheckSite,
  useSecurityStats,
  useSetAlertsVex,
  useUpdateAlertStatus,
  useBulkUpdateAlertStatus,
} from '@/hooks/useApi'
import type { AlertSiteSummary, SecurityAlert } from '@/api/client'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Spinner } from '@/components/ui/spinner'
import {
  ShieldAlert,
  AlertTriangle,
  CheckCircle2,
  BellOff,
  Zap,
  Filter,
  ChevronDown,
  ChevronRight,
  Github,
  ArrowLeft,
  Bot,
  Copy,
  RefreshCw,
  Globe,
} from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { cn } from '@/lib/utils'
import { nl } from 'date-fns/locale'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SEVERITY_STYLES: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
  high: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
  medium: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
  low: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
  unknown: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
}

const STATUS_LABELS: Record<string, string> = {
  new: 'Nieuw',
  acked: 'Gezien',
  resolved: 'Opgelost',
  deferred: 'Wacht op fix',
}

function severityLabel(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

function epssLabel(fraction: number | null): string {
  if (fraction === null) return ''
  // fraction is the raw EPSS probability (0.0–1.0); show as a percentage.
  const pct = fraction * 100
  // 2 decimals keeps sub-1% scores meaningful (e.g. 0.04%), trim trailing zeros.
  return `EPSS ${pct.toFixed(2).replace(/\.?0+$/, '')}%`
}

const KIND_LABELS: Record<string, string> = {
  cve: 'CVE',
  downtime: 'Downtime',
  security: 'Security',
  gsc: 'GSC',
  regression: 'Regressie',
  maintenance: 'Systeem',
}
const KIND_STYLES: Record<string, string> = {
  cve: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
  downtime: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
  security: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
  gsc: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  regression: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  maintenance: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
}

// ─── VEX: "not affected" with a reason ────────────────────────────────────────

/**
 * Closes CVE alerts with a recorded reason (VEX). The reason is required:
 * it is what tells you, months later, why a CVE in production was accepted.
 * It holds for this package version — a new version is judged again.
 */
function VexForm({ alertIds, onDone }: { alertIds: number[]; onDone: () => void }) {
  const [status, setStatus] = useState<'not_affected' | 'false_positive'>('not_affected')
  const [justification, setJustification] = useState('')
  const vex = useSetAlertsVex()
  const valid = justification.trim().length >= 10

  const submit = () =>
    vex.mutate(
      { ids: alertIds, status, justification: justification.trim() },
      {
        onSuccess: (r) => {
          toast.success(
            `${r.updated} alert(s) gesloten` + (r.skipped ? `, ${r.skipped} zonder huidige component overgeslagen` : ''),
          )
          onDone()
        },
        onError: (e) => toast.error(`Mislukt: ${e.message}`),
      },
    )

  return (
    <div className="mt-3 space-y-2 rounded-md border p-3">
      <div className="flex flex-wrap gap-2">
        {(['not_affected', 'false_positive'] as const).map((s) => (
          <Button key={s} size="sm" variant={status === s ? 'default' : 'outline'} onClick={() => setStatus(s)}>
            {s === 'not_affected' ? 'Niet getroffen' : 'Vals positief'}
          </Button>
        ))}
      </div>
      <textarea
        value={justification}
        onChange={(e) => setJustification(e.target.value)}
        rows={3}
        placeholder="Waarom? Bv.: gosu draait enkel bij de start om rechten af te geven; de kwetsbare netwerk- en parsercode van Go wordt niet gebruikt."
        className="w-full rounded-md border bg-background p-2 text-sm"
      />
      <div className="flex gap-2">
        <Button size="sm" disabled={!valid || vex.isPending} onClick={submit}>
          {vex.isPending && <Spinner size="sm" className="mr-1" />}
          {alertIds.length} alert(s) sluiten
        </Button>
        <Button size="sm" variant="ghost" onClick={onDone}>
          Annuleren
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Geldt voor deze versie van het pakket. Komt er een nieuwe versie, dan wordt die opnieuw beoordeeld.
      </p>
    </div>
  )
}

// ─── CVE group ────────────────────────────────────────────────────────────────

/**
 * One package, all its advisories. Trivy reports a CVE per advisory, so a
 * single outdated dependency produced six near-identical cards competing for
 * attention — while the action is one upgrade. Collapsed by default; the
 * severity shown is the worst in the group, because that is what decides
 * urgency.
 */
function CveGroupCard({
  packageKey,
  rows,
  open,
  onToggle,
  onAckAll,
  onAck,
  onResolve,
  isPending,
}: {
  packageKey: string
  rows: SecurityAlert[]
  open: boolean
  onToggle: () => void
  onAckAll: () => void
  onAck: (id: number) => void
  onResolve: (id: number) => void
  isPending: boolean
}) {
  const severityRank = ['critical', 'high', 'medium', 'low', 'unknown']
  const worst = rows
    .slice()
    .sort((a, b) => severityRank.indexOf(a.severity) - severityRank.indexOf(b.severity))[0]
  const kevCount = rows.filter((r) => r.kev).length
  const openCount = rows.filter((r) => r.status === 'new').length
  const [vexOpen, setVexOpen] = useState(false)
  const triageable = rows.filter((r) => r.status !== 'resolved').map((r) => r.id)

  // One upgrade closes the whole group, so the highest fixing version across
  // its CVEs is the number that matters — not one per row.
  const fixVersion = rows
    .map((r) => r.fixed_version)
    .filter((v): v is string => !!v)
    .sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }),
    )
    .at(-1)

  const repo = rows.find((r) => r.github_repo)?.github_repo
  const dossierName = rows.find((r) => r.dossier_name)?.dossier_name
  const issueUrl = repo
    ? `https://github.com/${repo}/issues/new?` +
      new URLSearchParams({
        title: `Upgrade ${packageKey}${fixVersion ? ` naar ${fixVersion}` : ''}`,
        body:
          `${rows.length} CVE's op ${packageKey}` +
          (dossierName ? ` (${dossierName})` : '') +
          `:\n\n` +
          rows
            .map(
              (r) =>
                `- ${r.vuln_id_str ?? r.dedup_key} (${r.severity}${r.kev ? ', KEV' : ''})`,
            )
            .join('\n') +
          (fixVersion ? `\n\nFix: upgrade naar ${fixVersion}.` : ''),
      }).toString()
    : null

  return (
    <Card className={kevCount > 0 ? 'border-red-500 dark:border-red-600' : ''}>
      <CardContent className="py-3">
        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={onToggle}
            className="flex min-w-0 flex-1 items-center gap-2 text-left"
            aria-expanded={open}
          >
            {open ? (
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            )}
            <span className="truncate font-mono text-sm font-medium">{packageKey}</span>
            <Badge className={SEVERITY_STYLES[worst.severity] ?? SEVERITY_STYLES.unknown}>
              {severityLabel(worst.severity)}
            </Badge>
            <span className="text-sm text-muted-foreground">
              {rows.length} CVE&apos;s
            </span>
            {kevCount > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                <Zap className="h-3 w-3" />
                {kevCount} KEV
              </span>
            )}
          </button>

          {issueUrl && (
            <a
              href={issueUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-sm transition-colors hover:bg-muted"
              title={`Issue aanmaken in ${repo}`}
            >
              <Github className="h-3.5 w-3.5" />
              Issue aanmaken
            </a>
          )}

          {openCount > 0 && (
            <Button size="sm" variant="outline" onClick={onAckAll} disabled={isPending}>
              Alles gezien ({openCount})
            </Button>
          )}
          {triageable.length > 0 && !vexOpen && (
            <Button size="sm" variant="ghost" onClick={() => setVexOpen(true)}>
              Niet getroffen…
            </Button>
          )}
        </div>

        {vexOpen && <VexForm alertIds={triageable} onDone={() => setVexOpen(false)} />}

        {fixVersion && (
          <p className="mt-2 text-sm">
            <span className="text-muted-foreground">Fix: </span>
            <span className="font-medium text-green-600 dark:text-green-300">
              upgrade naar {fixVersion}
            </span>
            <span className="text-muted-foreground"> — sluit alle {rows.length} CVE&apos;s</span>
          </p>
        )}

        {open && (
          <div className="mt-3 space-y-2 border-t pt-3">
            {rows.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAck={onAck}
                onResolve={onResolve}
                isPending={isPending}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Alert card ───────────────────────────────────────────────────────────────

function AlertCard({
  alert,
  onAck,
  onResolve,
  isPending,
}: {
  alert: SecurityAlert
  onAck: (id: number) => void
  onResolve: (id: number) => void
  isPending: boolean
}) {
  const isNew = alert.status === 'new'
  const isAcked = alert.status === 'acked'
  const [vexOpen, setVexOpen] = useState(false)

  return (
    <Card className={alert.kev ? 'border-red-500 dark:border-red-600' : ''}>
      <CardContent className="py-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            {/* Title row */}
            <div className="flex flex-wrap items-center gap-2">
              {alert.kev && (
                <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                  <Zap className="h-3 w-3" />
                  KEV
                </span>
              )}
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${KIND_STYLES[alert.kind] ?? KIND_STYLES.cve}`}>
                {KIND_LABELS[alert.kind] ?? alert.kind}
              </span>
              <Badge
                className={SEVERITY_STYLES[alert.severity] ?? SEVERITY_STYLES.unknown}
                variant="outline"
              >
                {severityLabel(alert.severity)}
              </Badge>
              {alert.kind === 'cve' && alert.vuln_id_str && (
                <code className="rounded bg-muted px-1.5 py-0.5 text-sm font-mono font-semibold">
                  {alert.vuln_id_str}
                </code>
              )}
              <Badge variant="secondary" className="text-xs">
                {STATUS_LABELS[alert.status] ?? alert.status}
              </Badge>
            </div>

            {/* Body: CVE → component line; other kinds → title */}
            {alert.kind === 'cve' ? (
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">
                  In{' '}
                  <span className="font-medium text-foreground">
                    {alert.component_name}
                  </span>
                  {' '}
                  <span className="font-mono text-xs">@{alert.component_version}</span>
                  {alert.dossier_name && alert.dossier_id !== null && (
                    <>
                      {' op '}
                      <Link
                        to={`/dossiers/${alert.dossier_id}`}
                        className="font-medium text-primary hover:underline"
                      >
                        {alert.dossier_name}
                      </Link>
                    </>
                  )}
                </p>
                {alert.fixed_version && (
                  <p className="text-sm">
                    <span className="text-muted-foreground">Fix: </span>
                    <span className="font-medium text-green-600 dark:text-green-300">
                      upgrade naar {alert.fixed_version}
                    </span>
                  </p>
                )}
              </div>
            ) : (
              <div className="space-y-1">
                {alert.title && (
                  <p className="text-sm font-medium text-foreground">{alert.title}</p>
                )}
                {alert.message && (
                  <p className="text-sm text-muted-foreground">{alert.message}</p>
                )}
              </div>
            )}

            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
              {alert.epss_score !== null && (
                <span className="text-orange-600 dark:text-orange-400 font-medium">
                  {epssLabel(alert.epss_score)} exploitkans
                </span>
              )}
              <span>
                {formatDistanceToNow(new Date(alert.created_at), {
                  addSuffix: true,
                  locale: nl,
                })}
              </span>
              {alert.notified_at && (
                <span>
                  Gemeld{' '}
                  {formatDistanceToNow(new Date(alert.notified_at), {
                    addSuffix: true,
                    locale: nl,
                  })}
                </span>
              )}
              {/* Whether the finding still applies. Null means the nightly
                  re-scan has not confirmed it since this field started being
                  written — not that it is gone. */}
              <span className={alert.last_seen_at ? '' : 'text-amber-600 dark:text-amber-400'}>
                {alert.last_seen_at
                  ? `Laatst gezien ${formatDistanceToNow(new Date(alert.last_seen_at), { addSuffix: true, locale: nl })}`
                  : 'Nog niet herbevestigd door een scan'}
              </span>
            </div>
          </div>

          {/* Actions */}
          {alert.status !== 'resolved' && (
            <div className="flex shrink-0 gap-2">
              {isNew && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => onAck(alert.id)}
                  title="Markeer als gezien"
                >
                  {isPending ? (
                    <Spinner size="sm" className="mr-1" />
                  ) : (
                    <CheckCircle2 className="mr-1 h-4 w-4" />
                  )}
                  Gezien
                </Button>
              )}
              {alert.kind === 'cve' && !vexOpen && (
                <Button size="sm" variant="ghost" onClick={() => setVexOpen(true)}>
                  Niet getroffen…
                </Button>
              )}
              {(isNew || isAcked) && (
                <Button
                  size="sm"
                  variant="outline"
                  disabled={isPending}
                  onClick={() => onResolve(alert.id)}
                  title="Markeer als opgelost"
                >
                  {isPending ? (
                    <Spinner size="sm" className="mr-1" />
                  ) : (
                    <BellOff className="mr-1 h-4 w-4" />
                  )}
                  Opgelost
                </Button>
              )}
            </div>
          )}
        </div>
        {vexOpen && <VexForm alertIds={[alert.id]} onDone={() => setVexOpen(false)} />}
      </CardContent>
    </Card>
  )
}

// ─── Per site ─────────────────────────────────────────────────────────────────

function useRecheck() {
  const recheck = useRecheckSite()
  const run = (dossierId: number, name: string) =>
    recheck.mutate(dossierId, {
      onSuccess: (r) =>
        toast.success(
          `${name} opnieuw gecontroleerd: ${r.new_alerts ?? 0} nieuw, ` +
            `${r.resolved_alerts ?? 0} opgelost`,
        ),
      onError: (e) => toast.error(`Controle mislukt: ${e.message}`),
    })
  const isRunning = (dossierId: number | null) =>
    recheck.isPending && recheck.variables === dossierId
  return { run, isRunning }
}

function RecheckButton({
  site,
  recheck,
}: {
  site: AlertSiteSummary
  recheck: ReturnType<typeof useRecheck>
}) {
  if (site.dossier_id === null) return null
  const id = site.dossier_id
  const running = recheck.isRunning(id)
  return (
    <Button
      size="sm"
      variant="outline"
      disabled={running}
      onClick={() => recheck.run(id, site.name)}
      title="Evalueer de huidige inventaris opnieuw tegen de CVE-databank"
    >
      <RefreshCw className={cn('mr-1 h-4 w-4', running && 'animate-spin')} />
      {running ? 'Bezig…' : 'Opnieuw controleren'}
    </Button>
  )
}

function SiteCard({
  site,
  onOpen,
  onInstruction,
  recheck,
}: {
  site: AlertSiteSummary
  onOpen: () => void
  onInstruction: () => void
  recheck: ReturnType<typeof useRecheck>
}) {
  const kinds = Object.entries(site.by_kind).sort((a, b) => b[1] - a[1])
  return (
    <Card className={site.kev > 0 ? 'border-red-500 dark:border-red-600' : ''}>
      <CardContent className="flex flex-col gap-3 py-4 sm:flex-row sm:items-center sm:justify-between">
        <button onClick={onOpen} className="flex min-w-0 flex-1 flex-col gap-1 text-left">
          <span className="flex items-center gap-2 font-medium">
            <Globe className="h-4 w-4 shrink-0 text-muted-foreground" />
            <span className="truncate">{site.name}</span>
          </span>
          {site.url && (
            <span className="truncate text-xs text-muted-foreground">{site.url}</span>
          )}
          <span className="flex flex-wrap items-center gap-2 pt-1">
            {site.kev > 0 && (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white">
                <Zap className="h-3 w-3" />
                {site.kev} KEV
              </span>
            )}
            {site.critical > 0 && (
              <Badge className={SEVERITY_STYLES.critical}>{site.critical} critical</Badge>
            )}
            {site.high > 0 && <Badge className={SEVERITY_STYLES.high}>{site.high} high</Badge>}
            <span className="text-sm text-muted-foreground">
              {site.open} open
              {site.deferred > 0 && ` · ${site.deferred} wacht op fix`}
            </span>
            {kinds.map(([kind, count]) => (
              <span
                key={kind}
                className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${KIND_STYLES[kind] ?? KIND_STYLES.cve}`}
              >
                {KIND_LABELS[kind] ?? kind} {count}
              </span>
            ))}
          </span>
        </button>
        <div className="flex shrink-0 flex-wrap gap-2">
          {site.dossier_id !== null && site.open > 0 && (
            <Button size="sm" variant="outline" onClick={onInstruction}>
              <Bot className="mr-1 h-4 w-4" />
              Claude-instructie
            </Button>
          )}
          <RecheckButton site={site} recheck={recheck} />
          <Button size="sm" onClick={onOpen}>
            Bekijk
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

/** The Claude Code instruction for one site, generated from its open alerts. */
function ClaudeInstructionPanel({ dossierId, onClose }: { dossierId: number; onClose: () => void }) {
  const { data, isLoading, isError } = useClaudeInstruction(dossierId, true)
  const copy = async () => {
    if (!data) return
    try {
      await navigator.clipboard.writeText(data.text)
      toast.success('Instructie gekopieerd — plak ze in Claude Code in de repo van dit project')
    } catch {
      toast.error('Kopiëren mislukt; selecteer de tekst handmatig')
    }
  }
  return (
    <Card>
      <CardContent className="space-y-3 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-2 font-medium">
            <Bot className="h-4 w-4" />
            Instructie voor Claude Code
          </p>
          <div className="flex gap-2">
            <Button size="sm" onClick={copy} disabled={!data}>
              <Copy className="mr-1 h-4 w-4" />
              Kopiëren
            </Button>
            <Button size="sm" variant="outline" onClick={onClose}>
              Sluiten
            </Button>
          </div>
        </div>
        {isLoading ? (
          <div className="flex h-24 items-center justify-center">
            <Spinner />
          </div>
        ) : isError || !data ? (
          <p className="text-sm text-red-600">Instructie kon niet opgehaald worden.</p>
        ) : (
          <pre className="max-h-[28rem] overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 text-xs">
            {data.text}
          </pre>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Alert list (one site, or the system history) ─────────────────────────────

function AlertList({
  dossierId,
  noDossier,
  system,
}: {
  dossierId?: number
  noDossier?: boolean
  system?: boolean
}) {
  const [statusFilter, setStatusFilter] = useState<string>('new')
  const [kevOnly, setKevOnly] = useState(false)
  const [kindFilter, setKindFilter] = useState<string>('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const { data: alerts, isLoading } = useAlerts({
    status: statusFilter || undefined,
    kev_only: kevOnly || undefined,
    dossier_id: dossierId,
    no_dossier: noDossier || undefined,
    kind: system ? 'maintenance' : kindFilter || undefined,
    exclude_kind: system ? undefined : 'maintenance',
    limit: 200,
  })
  const updateStatus = useUpdateAlertStatus()
  const bulkUpdate = useBulkUpdateAlertStatus()

  // CVEs arrive one row per vulnerability, so a package with six advisories
  // renders as six near-identical cards. Grouping them makes the page read as
  // "one package to upgrade" instead.
  const cveGroups = useMemo(() => {
    const groups = new Map<string, SecurityAlert[]>()
    for (const alert of alerts ?? []) {
      if (alert.kind !== 'cve' || !alert.component_name) continue
      const key = `${alert.component_name}@${alert.component_version ?? '?'}`
      const bucket = groups.get(key)
      if (bucket) bucket.push(alert)
      else groups.set(key, [alert])
    }
    // A single CVE is not a group — it renders as a plain card below.
    return [...groups.entries()].filter(([, rows]) => rows.length > 1)
  }, [alerts])

  const groupedIds = useMemo(
    () => new Set(cveGroups.flatMap(([, rows]) => rows.map((a) => a.id))),
    [cveGroups],
  )
  const ungrouped = (alerts ?? []).filter((a) => !groupedIds.has(a.id))

  const toggleGroup = (key: string) =>
    setExpanded((prev) => {
      const next = new Set(prev)
      next.has(key) ? next.delete(key) : next.add(key)
      return next
    })

  const handleGroupAck = (rows: SecurityAlert[]) => {
    const open = rows.filter((a) => a.status === 'new').map((a) => a.id)
    if (open.length === 0) return
    bulkUpdate.mutate(
      { ids: open, status: 'acked' },
      {
        onSuccess: (r) => toast.success(`${r.updated} alert(s) gemarkeerd als gezien`),
        onError: () => toast.error('Bijwerken mislukt'),
      },
    )
  }

  const handleAck = async (id: number) => {
    try {
      await updateStatus.mutateAsync({ id, status: 'acked' })
      toast.success('Gemarkeerd als gezien')
    } catch {
      toast.error('Fout bij bijwerken')
    }
  }

  const handleResolve = async (id: number) => {
    try {
      await updateStatus.mutateAsync({ id, status: 'resolved' })
      toast.success('Gemarkeerd als opgelost')
    } catch {
      toast.error('Fout bij bijwerken')
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        {(['new', 'acked', 'deferred', 'resolved', ''] as const).map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFilter === s ? 'default' : 'outline'}
            onClick={() => setStatusFilter(s)}
          >
            {s === '' ? 'Alles' : STATUS_LABELS[s]}
          </Button>
        ))}
        <Button
          size="sm"
          variant={kevOnly ? 'default' : 'outline'}
          onClick={() => setKevOnly(!kevOnly)}
          className="ml-2"
        >
          <Zap className="mr-1 h-3 w-3" />
          Alleen KEV
        </Button>
        {/* Kind filter — maintenance has its own tab, so it is not listed */}
        <div className={cn('ml-auto flex flex-wrap gap-1', system && 'hidden')}>
          {(['', 'cve', 'downtime', 'security', 'gsc', 'regression'] as const).map((k) => (
            <Button
              key={k || 'all'}
              size="sm"
              variant={kindFilter === k ? 'default' : 'outline'}
              onClick={() => setKindFilter(k)}
            >
              {k === '' ? 'Alle types' : KIND_LABELS[k]}
            </Button>
          ))}
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : alerts?.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-green-500" />
            <p className="font-medium">Geen alerts</p>
            <p className="text-sm text-muted-foreground">
              {statusFilter === 'new'
                ? 'Geen nieuwe beveiligingswaarschuwingen.'
                : 'Geen alerts met dit filter.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {cveGroups.map(([key, rows]) => (
            <CveGroupCard
              key={key}
              packageKey={key}
              rows={rows}
              open={expanded.has(key)}
              onToggle={() => toggleGroup(key)}
              onAckAll={() => handleGroupAck(rows)}
              onAck={handleAck}
              onResolve={handleResolve}
              isPending={updateStatus.isPending || bulkUpdate.isPending}
            />
          ))}

          {/* KEV first */}
          {ungrouped
            .slice()
            .sort((a, b) => {
              if (a.kev !== b.kev) return a.kev ? -1 : 1
              const sevOrder = ['critical', 'high', 'medium', 'low', 'unknown']
              return sevOrder.indexOf(a.severity) - sevOrder.indexOf(b.severity)
            })
            .map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onAck={handleAck}
                onResolve={handleResolve}
                isPending={updateStatus.isPending}
              />
            ))}
        </div>
      )}
    </div>
  )
}

// ─── Main page ────────────────────────────────────────────────────────────────

export default function Alerts() {
  // The page starts at the site: which one needs work, and how much. A
  // site's own alerts are one click further (?site=<dossier id>, or "none"
  // for alerts that belong to no site).
  const [searchParams, setSearchParams] = useSearchParams()
  const siteParam = searchParams.get('site')
  const showInstruction = searchParams.get('instructie') === '1'
  const [tab, setTab] = useState<'sites' | 'system'>('sites')

  const { data: stats } = useAlertStats()
  const { data: scanFindings } = useSecurityStats()
  const { data: sites, isLoading: sitesLoading } = useAlertSites()
  const siteDossierId = siteParam && siteParam !== 'none' ? Number(siteParam) : null
  const { data: siteData } = useAlertSite(siteDossierId)
  const recheck = useRecheck()

  const openSite = (id: number | null, instruction = false) => {
    const next = new URLSearchParams({ site: id === null ? 'none' : String(id) })
    if (instruction) next.set('instructie', '1')
    setSearchParams(next)
  }

  if (siteParam) {
    const dossierId = siteParam === 'none' ? null : Number(siteParam)
    const site =
      (dossierId !== null ? siteData : sites?.find((s) => s.dossier_id === null)) ??
      ({
        dossier_id: dossierId,
        name: dossierId === null ? 'Zonder website' : `Website #${dossierId}`,
        url: null,
        github_repo: null,
        open: 0,
        kev: 0,
        critical: 0,
        high: 0,
        deferred: 0,
        by_kind: {},
        last_inventory_at: null,
      } satisfies AlertSiteSummary)
    const toggleInstruction = (open: boolean) => {
      const next = new URLSearchParams(searchParams)
      if (open) next.set('instructie', '1')
      else next.delete('instructie')
      setSearchParams(next)
    }

    return (
      <div className="space-y-6">
        <div className="space-y-2">
          <Button size="sm" variant="ghost" onClick={() => setSearchParams({})}>
            <ArrowLeft className="mr-1 h-4 w-4" />
            Alle websites
          </Button>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-3xl font-bold">{site.name}</h1>
              {site.url && <p className="text-muted-foreground">{site.url}</p>}
              {dossierId !== null && (
                <p className="text-xs text-muted-foreground">
                  {site.last_inventory_at
                    ? `Inventaris ${formatDistanceToNow(new Date(site.last_inventory_at), { addSuffix: true, locale: nl })} gerapporteerd`
                    : 'Nog geen inventaris gerapporteerd'}
                  {' · '}
                  <Link to={`/inventory/${dossierId}`} className="text-primary hover:underline">
                    Componenten
                  </Link>
                </p>
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              {dossierId !== null && (
                <Button
                  size="sm"
                  variant={showInstruction ? 'default' : 'outline'}
                  onClick={() => toggleInstruction(!showInstruction)}
                >
                  <Bot className="mr-1 h-4 w-4" />
                  Claude-instructie
                </Button>
              )}
              <RecheckButton site={site} recheck={recheck} />
            </div>
          </div>
        </div>

        {showInstruction && dossierId !== null && (
          <ClaudeInstructionPanel
            dossierId={dossierId}
            onClose={() => toggleInstruction(false)}
          />
        )}

        <AlertList
          key={siteParam}
          dossierId={dossierId ?? undefined}
          noDossier={dossierId === null}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold">Alerts</h1>
        <p className="text-muted-foreground">Per website: wat open staat en wat eerst moet</p>
      </div>

      {/* Stats cards */}
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Card>
          <CardContent className="py-4">
            <p className="text-xs text-muted-foreground">Nieuwe alerts</p>
            <p className="text-2xl font-bold">{stats?.total_new ?? '—'}</p>
          </CardContent>
        </Card>
        <Card className={stats?.kev ? 'border-red-500' : ''}>
          <CardContent className="py-4">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <Zap className="h-3 w-3 text-red-500" />
              KEV (actief misbruikt)
            </p>
            <p className="text-2xl font-bold text-red-600 dark:text-red-400">
              {stats?.kev ?? '—'}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <ShieldAlert className="h-3 w-3 text-red-400" />
              Critical
            </p>
            <p className="text-2xl font-bold">{stats?.critical ?? '—'}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="py-4">
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <AlertTriangle className="h-3 w-3 text-orange-400" />
              High
            </p>
            <p className="text-2xl font-bold">{stats?.high ?? '—'}</p>
          </CardContent>
        </Card>
      </div>

      {/* Scan findings — a different source from the CVE alerts above: these
          come from the ZAP/Nuclei scans on the sites themselves, not from
          Trivy's dependency inventory. */}
      <Card>
        <CardContent className="flex flex-wrap items-center gap-x-8 gap-y-3 py-4">
          <div>
            <p className="text-xs font-medium">Bevindingen uit security-scans</p>
            <p className="text-xs text-muted-foreground">ZAP en Nuclei, over alle scans</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Totaal</p>
            <p className="text-xl font-bold">{scanFindings?.total_findings ?? '—'}</p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Kritiek</p>
            <p className="text-xl font-bold text-red-600 dark:text-red-400">
              {scanFindings?.critical_count ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">Hoog</p>
            <p className="text-xl font-bold text-orange-600 dark:text-orange-400">
              {scanFindings?.high_count ?? '—'}
            </p>
          </div>
          <div>
            <p className="text-xs text-muted-foreground">False positives</p>
            <p className="text-xl font-bold text-muted-foreground">
              {scanFindings?.false_positives ?? '—'}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Sites vs. system history */}
      <div className="flex gap-1 border-b">
        {([
          ['sites', 'Websites', sites?.length],
          ['system', 'Systeem', stats?.maintenance],
        ] as const).map(([key, label, count]) => (
          <button
            key={key}
            onClick={() => setTab(key)}
            className={cn(
              '-mb-px border-b-2 px-4 py-2 text-sm font-medium transition-colors',
              tab === key
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            {label}
            {count !== undefined && count > 0 && (
              <span className="ml-1.5 text-xs text-muted-foreground">{count}</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'system' ? (
        <>
          <p className="text-sm text-muted-foreground">
            Onderhoudsgebeurtenissen — informatief, niets om af te handelen. Nieuwe
            image-wijzigingen worden hier niet meer als alert aangemaakt maar in de
            maintenance-log geschreven; dit is de historie van vóór die wijziging.
          </p>
          <AlertList system />
        </>
      ) : sitesLoading ? (
        <div className="flex h-40 items-center justify-center">
          <Spinner size="lg" />
        </div>
      ) : !sites || sites.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CheckCircle2 className="mx-auto mb-3 h-10 w-10 text-green-500" />
            <p className="font-medium">Geen open alerts</p>
            <p className="text-sm text-muted-foreground">Geen enkele website heeft iets openstaan.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {sites.map((site) => (
            <SiteCard
              key={site.dossier_id ?? 'none'}
              site={site}
              onOpen={() => openSite(site.dossier_id)}
              onInstruction={() => openSite(site.dossier_id, true)}
              recheck={recheck}
            />
          ))}
        </div>
      )}
    </div>
  )
}
